/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Center } from 'chakra-v3';
import type { BoxProps } from 'chakra-v3';

interface CardShellProps extends BoxProps {
  children: React.ReactNode;
  blurBgColor?: BoxProps['bg'];
  blurAmount?: string;
  showBlurOnMobile?: boolean;
  blurProps?: BoxProps;
  contentProps?: BoxProps;
}

/**
 * CardShell component provides a decorative blur background with content overlay.
 * Useful for creating visually appealing card containers with depth.
 *
 * @param children - The content to render inside the card
 * @param blurBgColor - Background color for the blur layer (default: 'blurShadowBlue')
 * @param blurAmount - CSS blur amount (default: '75px')
 * @param showBlurOnMobile - Whether to show blur on mobile viewports (default: false)
 * @param blurProps - Additional props to pass to the blur layer Box component
 * @param contentProps - Additional props to pass to the content container Box component
 *
 * @example
 * ```tsx
 * <CardShell blurAmount="100px">
 *   <MyContent />
 * </CardShell>
 * ```
 */
const CardShell: React.FC<CardShellProps> = ({
  children,
  blurBgColor = 'blurShadowBlue',
  blurAmount = '75px',
  showBlurOnMobile = false,
  blurProps,
  contentProps,
  ...rest
}) => (
  <Box position="relative" {...rest}>
    <Box
      position="absolute"
      left="50%"
      top="50%"
      transform="translate(-50%, -50%)"
      w="100%"
      h="100%"
      filter={`blur(${blurAmount})`}
      bg={blurBgColor}
      pointerEvents="none"
      display={showBlurOnMobile ? 'block' : { base: 'none', md: 'none', lg: 'block' }}
      zIndex={0}
      {...blurProps}
    />
    <Center bg="bg.primary" w="100%" h="100%" position="relative" zIndex={1} {...contentProps}>
      {children}
    </Center>
  </Box>
);

export default CardShell;
